import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { demands, type Demand, type DemandSummary } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { KpiCard } from '@/shared/ui/KpiCard';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';

import { PartyToolbar } from '../shared/PartyToolbar';

const etapas = [
  { value: '', label: 'Todas as etapas' },
  'aguardando_credito',
  'em_subscricao',
  'em_juridico',
  'aguardando_comercial',
  'minuta_gerada',
  'aguardando_ccg',
  'aguardando_assinatura',
  'em_emissao',
].map((item) =>
  typeof item === 'string'
    ? { value: item, label: statusLabel('operacao', item) }
    : item,
);

const prioridades = [
  { value: '', label: 'Todas as prioridades' },
  { value: 'urgente', label: 'Urgente' },
  { value: 'alta', label: 'Alta' },
  { value: 'normal', label: 'Normal' },
  { value: 'baixa', label: 'Baixa' },
];

const limite = 25;

/**
 * Demandas e SLA.
 *
 * A fila e a propria esteira vista pelo prazo. No material de referencia, demanda
 * e operacao sao a mesma coisa: o que muda e a pergunta que se faz a ela. Por isso
 * cada linha leva a Operacao 360, em vez de abrir um cadastro paralelo.
 *
 * A ordem vem do servidor, ja calculada: prazo primeiro, prioridade como
 * desempate. Ordenar aqui ordenaria apenas a pagina visivel, e a operacao mais
 * urgente poderia estar na terceira pagina.
 */
export function DemandsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [busca, setBusca] = useState('');
  const [etapa, setEtapa] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [somenteVencidas, setSomenteVencidas] = useState(false);
  const [offset, setOffset] = useState(0);

  const podeVer = can('demandas.demanda.view');

  const consulta = useQuery({
    queryKey: ['demands', { busca, etapa, prioridade, somenteVencidas, offset }],
    queryFn: ({ signal }) =>
      demands.list(
        {
          q: busca,
          stage: etapa,
          priority: prioridade,
          overdue: somenteVencidas ? 'true' : undefined,
          limit: limite,
          offset,
        },
        signal,
      ),
    enabled: podeVer,
  });

  const itens = consulta.data?.items ?? [];
  // Os indicadores vem do servidor, somados na fila inteira com os filtros (defeito
  // 17 de "Operacao e esteira" do 47, anexo C): antes somavam so a pagina visivel.
  const resumo = consulta.data?.meta.summary as DemandSummary | undefined;
  const vencidas =
    resumo?.overdue ?? itens.filter((demanda) => demanda.sla.overdue).length;
  const vencendo =
    resumo?.due_soon ??
    itens.filter(
      (demanda) =>
        demanda.sla.has_target && !demanda.sla.overdue && demanda.sla.days_left <= 2,
    ).length;
  const comPendencia =
    resumo?.with_pendings ??
    itens.filter((demanda) => demanda.open_pendings > 0).length;
  const alcance = resumo ? 'Na fila inteira, com os filtros' : 'Nesta pagina da fila';

  const colunas: Column<Demand>[] = [
    {
      key: 'demanda',
      header: 'Demanda',
      render: (demanda) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-ink-900">
            <span className="tabular-nums">{demanda.number}</span>
            <StatusBadge kind="prioridade" value={demanda.priority} />
          </p>
          <p className="truncate text-2xs text-ink-500">{demanda.policyholder.label}</p>
        </div>
      ),
    },
    {
      key: 'etapa',
      header: 'Etapa',
      render: (demanda) => <StatusBadge kind="operacao" value={demanda.stage} />,
    },
    {
      key: 'responsavel',
      header: 'Responsavel',
      hideBelow: 'lg',
      render: (demanda) =>
        demanda.owner_label || <span className="text-ink-400">sem responsavel</span>,
    },
    {
      key: 'pendencias',
      header: 'Pendencias',
      align: 'right',
      hideBelow: 'md',
      render: (demanda) =>
        demanda.open_pendings > 0 ? (
          <Badge tone="warning">{demanda.open_pendings}</Badge>
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'conversa',
      header: 'Conversa',
      align: 'right',
      hideBelow: 'lg',
      render: (demanda) =>
        demanda.comments_count ? (
          <span className="tabular-nums text-ink-700">{demanda.comments_count}</span>
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'prazo',
      header: 'Prazo',
      align: 'right',
      render: (demanda) => <PrazoCelula demanda={demanda} />,
    },
  ];

  if (!podeVer) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Demandas e SLA" />
        <Alert tone="info" title="Fila indisponivel para o seu acesso">
          O seu perfil nao inclui a visualizacao da fila de trabalho.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Demandas e SLA"
        description="O que precisa de acao hoje, na ordem do prazo."
        actions={
          can('esteiras.esteira.view') ? (
            <Link to="/esteiras" className={buttonClassName({ variant: 'secondary' })}>
              Ver as esteiras
            </Link>
          ) : undefined
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a fila">
          {consulta.error.message}
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard
          icon="alert"
          tone="danger"
          highlight={vencidas > 0}
          label="Prazo vencido"
          value={String(vencidas)}
          hint={alcance}
          loading={consulta.isPending}
        />
        <KpiCard
          icon="clock"
          tone="warning"
          highlight={vencendo > 0}
          label="Vence em ate dois dias"
          value={String(vencendo)}
          hint={alcance}
          loading={consulta.isPending}
        />
        <KpiCard
          icon="file"
          tone="warning"
          highlight={comPendencia > 0}
          label="Com pendencia aberta"
          value={String(comPendencia)}
          hint="Aguardando resposta de alguma area"
          loading={consulta.isPending}
        />
      </div>

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero, objeto, tomador ou corretora"
          filter={
            <div className="flex flex-wrap gap-2">
              <Select
                label="Etapa"
                className="h-9"
                options={etapas}
                value={etapa}
                onChange={(event) => {
                  setEtapa(event.target.value);
                  setOffset(0);
                }}
              />
              <Select
                label="Prioridade"
                className="h-9"
                options={prioridades}
                value={prioridade}
                onChange={(event) => {
                  setPrioridade(event.target.value);
                  setOffset(0);
                }}
              />
            </div>
          }
          extra={
            <label className="flex h-9 cursor-pointer items-center gap-2 self-end text-xs text-ink-600">
              <input
                type="checkbox"
                checked={somenteVencidas}
                onChange={(event) => {
                  setSomenteVencidas(event.target.checked);
                  setOffset(0);
                }}
                className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
              />
              Somente prazo vencido
            </label>
          }
        />

        <DataTable
          columns={colunas}
          items={itens}
          keyOf={(demanda) => demanda.id}
          loading={consulta.isPending}
          onRowClick={(demanda) => void navigate(`/operacoes/${demanda.id}`)}
          empty={
            <EmptyState
              icon="clock"
              title={
                busca || etapa || prioridade || somenteVencidas
                  ? 'Nenhuma demanda encontrada'
                  : 'Nenhuma demanda em andamento'
              }
              description={
                busca || etapa || prioridade || somenteVencidas
                  ? 'Ajuste os filtros para ver outras demandas.'
                  : 'A fila reune as operacoes que ainda precisam de acao. Ela se preenche conforme as operacoes sao abertas.'
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}

/**
 * Prazo da demanda.
 *
 * O numero de dias vem calculado do servidor. A cor traduz a urgencia sem exigir
 * que a pessoa compare datas de cabeca, que e o que a fila existe para evitar.
 */
function PrazoCelula({ demanda }: { demanda: Demand }) {
  if (!demanda.sla.has_target) {
    return <span className="text-2xs text-ink-400">sem prazo</span>;
  }

  const dias = demanda.sla.days_left;
  const tom = demanda.sla.overdue
    ? 'text-danger-700'
    : dias <= 2
      ? 'text-warning-700'
      : 'text-ink-700';

  return (
    <div className="flex flex-col items-end">
      <span className={cn('text-sm font-semibold tabular-nums', tom)}>
        {demanda.sla.overdue
          ? `${Math.abs(dias)} d de atraso`
          : dias === 0
            ? 'vence hoje'
            : `${dias} d`}
      </span>
      <span className="text-2xs text-ink-500">{formatDate(demanda.sla.due_at)}</span>
    </div>
  );
}
